import api from "@/api";
import { fetchAndSeedPlansType } from "@/composables/usePlansType";
import {
  EMPTY_SCHEMA,
  absoluteUrl,
  defineTool,
  jsonResult,
  type WebMcpToolsContext,
} from "./shared";

/**
 * Tools de conversão: consultar planos/pacotes e ENTRAR no funil de assinatura.
 *
 * Nenhuma delas cobra nada. `start_subscription` só semeia a escolha nos mesmos
 * stores que os cards da página usam (`assineChoice`, `plans`) e leva para a
 * tela de períodos — a partir daí é o mesmo caminho de sempre: a pessoa escolhe
 * o período, preenche o pagamento e confirma.
 */
export function subscriptionTools(
  ctx: WebMcpToolsContext
): WebMcpToolDescriptor[] {
  const { router, language, t } = ctx;
  const text = t.tools;

  // Stores resolvidos na factory (contexto do Nuxt); ver nota em tools.catalog.
  const storePlan = planStore();
  const storeUser = userStore();
  const storeChoice = assineChoiceStore();

  const currency = language === "en" ? "USD" : "BRL";

  /**
   * Mesma resolução de `resolvePlansTypeParams()`, porém sem `useRoute()`: o
   * `execute` roda fora do contexto do Nuxt (quem chama é o browser), e ali
   * qualquer composable que dependa de `useNuxtApp()` estoura.
   */
  function plansParams() {
    const query = router.currentRoute.value.query;
    return {
      language,
      voucher: query.coupon
        ? ""
        : (query.voucher as string) || (storePlan.voucher as string) || "",
      coupon: query.voucher
        ? ""
        : (query.coupon as string) || (storePlan.coupon as string) || "",
      code_share: String(query.code_share ?? ""),
    };
  }

  async function ensurePlansType() {
    if (storePlan.plansType.length) return storePlan.plansType;
    await fetchAndSeedPlansType(plansParams());
    return storePlan.plansType;
  }

  return [
    defineTool(t, {
      name: "get_subscription_plans",
      description: text.get_subscription_plans.description,
      annotations: { readOnlyHint: true },
      inputSchema: EMPTY_SCHEMA,
      execute: async () => {
        const plansType = await ensurePlansType();
        if (!plansType.length) return t.messages.noResults;

        return jsonResult({
          currency,
          subscribeUrl: absoluteUrl(router, {
            name: "language-assine",
            params: { language },
          }),
          tiers: plansType.map((tier: any) => ({
            tier: tier.plan_type_name,
            includes: (tier.permissions ?? [])
              .filter((p: any) => p.active)
              .map((p: any) => p.name),
            options: (tier.plans ?? []).map((plan: any) => ({
              months: plan.months,
              period: plan.period,
              price: plan.value,
              discountPercent: plan.discount || 0,
              // O `discount_value` vem 0 da API quando não há cupom aplicado,
              // mesmo com `discount` > 0 — sem exigir que ele seja truthy, o
              // agente anunciava "plano anual por R$ 0,00". Mesmo guard que os
              // cards da página usam (AssinePlanCard.vue, isPromoCard).
              priceWithDiscount:
                plan.discount > 0 &&
                plan.discount_value &&
                plan.discount_value != +plan.value
                  ? plan.discount_value
                  : plan.value,
              downloadCredits: plan.credits ?? null,
            })),
          })),
        });
      },
    }),

    defineTool(t, {
      name: "start_subscription",
      description: text.start_subscription.description,
      inputSchema: {
        type: "object",
        properties: {
          plan_type: {
            type: "string",
            enum: ["Basic", "Plus", "Gold"],
            description: text.start_subscription.params.plan_type,
          },
          months: {
            type: "number",
            description: text.start_subscription.params.months,
          },
          coupon: {
            type: "string",
            description: text.start_subscription.params.coupon,
          },
        },
      },
      execute: async ({ plan_type, months, coupon }) => {
        if (coupon) {
          // Mesma ação que /assine dispara ao receber ?coupon_discount=CODE.
          await storePlan.setCupomDesconto(String(coupon));
        }

        const plansType = await ensurePlansType();
        const tier = plan_type
          ? plansType.find(
              (p: any) =>
                String(p.plan_type_name).toLowerCase() ===
                String(plan_type).toLowerCase()
            )
          : null;

        // Sem plano definido, a pessoa escolhe na própria tela de planos.
        if (!tier) {
          const to = { name: "language-assine", params: { language } };
          await router.push(to);
          return `${t.messages.navigated} ${absoluteUrl(router, to)}`;
        }

        await storeChoice.setAssineChoicePlanType({
          choicePlanTypeId: Number(tier.plan_type_id),
          choicePlanTypeName: String(tier.plan_type_name),
        });

        // Destino é /assine, não /assine/period: a etapa "Período" foi
        // incorporada à tela única e a rota antiga hoje é só um redirect 301
        // (pages/[language]/assine/period.vue). Mandar para lá fazia a tool
        // anunciar ao agente uma URL que redireciona.
        await router.push({
          name: "language-assine",
          params: { language },
          query: { acionador: "webmcp" },
        });

        const chosen = months
          ? (tier.plans ?? []).find((p: any) => Number(p.months) === Number(months))
          : null;

        return jsonResult({
          message: `${t.messages.navigated} ${
            window.location.origin + router.currentRoute.value.fullPath
          }`,
          tier: tier.plan_type_name,
          suggestedOption: chosen
            ? { months: chosen.months, period: chosen.period, price: chosen.value }
            : null,
          confirmation: t.messages.planSelectionOpen,
        });
      },
    }),

    defineTool(t, {
      name: "get_account_status",
      description: text.get_account_status.description,
      annotations: { readOnlyHint: true },
      inputSchema: EMPTY_SCHEMA,
      execute: async (_args, { signal }) => {
        if (!storeUser.isLoggedIn) {
          return jsonResult({ signedIn: false, hint: t.messages.notLoggedIn });
        }

        const { data } = await api.get(
          `auth/isOrderActiveUser?locale=${language}`,
          { signal }
        );
        const order = data?.orderActive;

        // `orderActive` é o Order inteiro (inclui valores e ids de gateway).
        // Escolhemos campo a campo de propósito: nada de e-mail, nome, cartão
        // ou cupom de compartilhamento — tool nenhuma devolve dado pessoal.
        return jsonResult({
          signedIn: true,
          subscriptionActive: Boolean(storeUser.isActiveUser),
          plan: order?.plan_type_name ?? storeUser.planTypeName ?? null,
          planName: order?.plan_name ?? null,
          validUntil: order?.finish ?? null,
          daysToExpire: order?.days_to_expire ?? null,
        });
      },
    }),
  ];
}
